import React, { useState } from 'react';
import styled from 'styled-components';
import { useOktaAuth } from '@okta/okta-react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import { Button, BaseModal, CKEditorInput, LoadingSpinner, dateTimeTranslate, styles } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import { Notes, useOktaQuery } from '../../data';

const StyledHistoryCard = styled.div`
&& {
    border: 1px solid ${styles.pSBC(-0.1, styles.theme.background.geyser)};
    padding: 10px;
    max-height: 271px;
    overflow-y: auto;
}
`;

const EditPlayerAuthoredNoteModal = ({ show, container, note, onSave, onHide, customTitle }) => {
    const { t: translate } = useTranslation();
    const { authState: { accessToken } } = useOktaAuth();
    const [localNote, setLocalNote] = useState({ ...note });
    const [saveWait, setSaveWait] = useState(false);
    const isDisabled = () => !localNote.Note || saveWait;

    const { data: history, isLoading: loadingHistory } = useOktaQuery(
        ['history', note.NoteID],
        (t) => Notes.getReportNoteHistory(t, note),
        {
            enabled: note.NoteID > 0,
        },
    );

    const save = async () => {
        setSaveWait(true);
        const updatedNote = await Notes.saveReportNote(accessToken, localNote);
        setSaveWait(false);
        onSave(updatedNote);
    };

    const ckedConfig = {
        toolbar: [
            ['Bold', 'Italic', 'Underline', 'TextColor', 'RemoveFormat'],
            ['NumberedList', 'BulletedList', 'Indent', 'Outdent'],
            ['Format', 'Font', 'FontSize'],
        ],
        removePlugins: ['elementspath', 'magicline'],
    };

    return (
        <BaseModal size={note.NoteID ? 'xl' : 'md'} container={container} show={show} onHide={onHide} backdrop="static">
            <BaseModal.Body className="left">
                {(loadingHistory || saveWait) && <LoadingSpinner />}
                {!loadingHistory && !saveWait && (
                    <Row>
                        <Col sm={note.NoteID ? 7 : 12}>
                            <h4>
                                {translate('components.notes.notes')}: {customTitle || note.Pitcher?.FullName || note.Batter?.FullName || translate('components.notes.my-notes')}
                            </h4>
                            <CKEditorInput
                                rows={4}
                                initData={localNote.Note}
                                onChange={(data) => setLocalNote({ ...localNote, Note: data })}
                                config={ckedConfig}
                            />
                        </Col>
                        {!!note.NoteID && (
                            <Col sm={5}>
                                <h4>{translate('components.notes.view-history')}</h4>
                                <StyledHistoryCard>
                                    {history.map((noteVersion, index) => (
                                        <div key={noteVersion.NoteID}>
                                            {index > 0 && <hr />}
                                            <div dangerouslySetInnerHTML={{ __html: noteVersion.Note }} />
                                            <div className="text-right text-muted">{dateTimeTranslate(translate, noteVersion.LastUpdateDate)}</div>
                                        </div>
                                    ))}
                                    {history.length === 0 && (
                                        <p>{translate('components.notes.no-older-version')}</p>
                                    )}
                                </StyledHistoryCard>
                            </Col>
                        )}
                    </Row>
                )}
            </BaseModal.Body>
            <BaseModal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saveWait}>{translate('common.cancel')}</Button>
                <Button variant="primary" onClick={() => save()} disabled={isDisabled()}>{translate('common.save')}</Button>
            </BaseModal.Footer>
        </BaseModal>
    );
};

EditPlayerAuthoredNoteModal.propTypes = {
    note: PropTypes.shape({
        NoteID: PropTypes.number,
        Note: PropTypes.string,
        Batter: PropTypes.shape({ PlayerID: PropTypes.number, FullName: PropTypes.string }),
        Pitcher: PropTypes.shape({ PlayerID: PropTypes.number, FullName: PropTypes.string }),
        LastUpdateDate: PropTypes.string,
    }).isRequired,
    onSave: PropTypes.func.isRequired,
    onHide: PropTypes.func.isRequired,
    customTitle: PropTypes.string,
    show: PropTypes.bool,
    container: PropTypes.any, // eslint-disable-line react/forbid-prop-types
};

EditPlayerAuthoredNoteModal.defaultProps = {
    show: false,
    container: undefined,
    customTitle: undefined,
};

export default EditPlayerAuthoredNoteModal;
